import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/helpers/utils'

const pillDropdownVariants = cva(
	cn(
		'inline-flex w-fit cursor-pointer items-center rounded-full border bg-white font-medium text-grey-1000 transition',
		'border-grey-400',
		'hover:not-in-data-disabled:border-grey-500',
		'in-data-pressed:not-in-data-disabled:border-grey-500',
		'in-data-popup-open:border-grey-400 in-data-popup-open:shadow-[0_0_0_1.5px_var(--color-grey-1000)]',
		'in-focus-visible:border-grey-1000 in-focus-visible:shadow-airbnb-focus-visible in-focus-visible:outline-none',
		'in-data-disabled:cursor-not-allowed in-data-disabled:border-grey-400 in-data-disabled:bg-white in-data-disabled:text-grey-500 in-data-disabled:[&>svg]:fill-grey-500'
	),
	{
		variants: {
			size: {
				sm: 'h-8 gap-2 px-2.5 py-2.5 text-xs/4 [&_svg]:size-3',
				md: 'h-10 gap-2 px-4 py-2.5 text-sm/4.5 [&_svg]:size-3',
				lg: 'h-12 gap-2 px-5 py-4 text-sm/4.5 [&_svg]:size-3'
			}
		},
		defaultVariants: {
			size: 'md'
		}
	}
)

export type PillDropdownProps = VariantProps<typeof pillDropdownVariants> &
	React.ComponentProps<'div'>
/**
 * Pill-shaped dropdown trigger, built on Base UI's Select trigger and styled to
 * the design system's Pill Dropdown spec.
 *
 * States:
 * - default — `grey-400` border on white
 * - hover — `grey-500` border
 * - pressed — `grey-500` border
 * - open ("selected") — 1.5px `grey-1000` ring, chevron flipped
 * - focus-visible — `grey-1000` border + airbnb focus shadow
 * - disabled — `grey-400` border, `grey-500` text and chevron
 *
 * Presentational only: it renders a plain element and reads every state from
 * the Base UI trigger that wraps it, so it must be placed inside a
 * `SelectTrigger` (with `bare`, which drops that trigger's own field chrome).
 *
 * @example
 * <Select value={value} onValueChange={setValue}>
 *   <SelectTrigger bare>
 *     <PillDropdown size='sm'>
 *       <SelectValue />
 *     </PillDropdown>
 *   </SelectTrigger>
 *   <SelectContent>…</SelectContent>
 * </Select>
 */
function PillDropdown({
	className,
	size,
	children,
	...props
}: PillDropdownProps) {
	return (
		<div
			className={cn(pillDropdownVariants({ size }), className)}
			data-slot='pill-dropdown'
			{...props}
		>
			{children}
		</div>
	)
}

export { PillDropdown, pillDropdownVariants }
